import React, { Children, cloneElement, isValidElement } from 'react'
import { reduxForm, Field } from 'redux-form'
import { connect } from 'react-redux'
import { compose } from 'redux';
import { Wrap, Title, WrapFooterBtns, Form, SubTitle } from './Styled'
import { Button, RenderField } from 'components/Form'
import { Space } from 'styles/GlobalStyle';

/**
 * Form (redux-form wrapper)
 * Generic form shell connected to redux-form. Handles submit, validation, title,
 * field rendering, and footer action buttons.
 *
 * **How it works**
 * - Requires a unique `form` name (redux-form key in store).
 * - `validations` → redux-form `validate`, `submit` → `onSubmit`, `initialValues` → form defaults.
 * - Content via **either** `children` (gets `form` prop injected) **or** a `fields` config array.
 * - `submitBtns` renders footer `Button` components.
 * - Reinitializes when `initialValues` change (`enableReinitialize`); keeps dirty fields on reinit.
 *
 * Wrapped with `reduxForm` + `connect`. Internal name: `FormWrap`.
 *
 * @param {Object} props
 * @param {string} props.form - Redux-form name (required).
 * @param {Function} props.submit - Submit handler passed to redux-form `onSubmit`.
 * @param {Function} [props.validations] - Redux-form validate function.
 * @param {Object} [props.initialValues] - Default form values.
 * @param {string} [props.title] - Form heading.
 * @param {string} [props.subTitle] - Subheading below title.
 * @param {Array<Object>} [props.fields] - Field configs (used when no `children`). Same shape as `Fields`.
 * @param {Array<Object>} [props.submitBtns] - Footer button configs passed to `Button`.
 * @param {string} [props.csswrap] - Layout class forwarded to `Wrap`.
 * @param {number|string} [props.rowGap] - Row gap in the fields container.
 * @param {React.ReactNode} [props.children] - Custom form content; each child receives `form` prop.
 * @param {Function} props.handleSubmit - Injected by redux-form; wraps the submit handler.
 * @example
 * // Fields-driven form
 * <Form form="createTicket" validations={validate} submit={onSubmit} fields={fields} submitBtns={[submitBtn]} />
 * @example
 * // Children-driven form with RenderFields
 * <Form form={formname} validations={validate} submit={onSubmit} submitBtns={[]}>
 *   <RenderFields fields={formFields} />
 * </Form>
 */
const FormWrap = (props) => {
    const { rowGap, children, csswrap, handleSubmit, subTitle, initialValues, submit, title, fields, submitBtns } = props

    return (
        <Form onSubmit={handleSubmit(submit)}>
            {title &&
                <div>
                    <Title>
                        {title}
                    </Title>
                    {subTitle &&
                        <SubTitle>
                            {subTitle}
                        </SubTitle>
                    }
                    <Space />
                </div>
            }
            <Wrap rowGap={rowGap} csswrap={csswrap}>
                {
                    Children.map(children, child => {
                        if (isValidElement(child)) {
                            return cloneElement(child, { form: props })
                        }
                        return child
                    })
                    ||
                    fields.map((field, index) =>
                        <Field
                            {...field}
                            key={index}
                            dynamicComponent={field.component}
                            component={RenderField}
                        />
                    )
                }
            </Wrap>
            <WrapFooterBtns>
                {submitBtns?.map((btn, index) =>
                    <Button key={index} {...btn} />
                )}
            </WrapFooterBtns>
        </Form>
    )


}

const mapStateToProps = (state, ownProps) => {
    const { form, validations, submit, initialValues } = ownProps
    return {
        form: form,
        validate: validations,
        onSubmit: submit,
        initialValues: initialValues
    }
}

export default compose(
    connect(mapStateToProps),
    reduxForm({
        destroyOnUnmount: false,
        keepDirtyOnReinitialize: true,
        enableReinitialize: true
    })
)(FormWrap);
